import {
  hasProjectAccess,
  permissionsAnywhere,
  resolveEffectivePermissions,
} from '@obraplan/core/domain/access/permission-resolver';
import type { PermissionKey } from '@obraplan/core/domain/access/permissions';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import { forbiddenError, type AppError } from '../errors/app-error';
import { ERROR_CODE, ERROR_MESSAGE } from '../errors/error-codes';
import type { Session } from '../session';

/**
 * Verificação de permissão e de escopo de obra.
 *
 * Chamada por **todo** caso de uso sensível, antes de qualquer leitura ou escrita
 * (RN-131). A interface usa as mesmas permissões apenas para esconder ações; quem
 * recusa é esta camada (RN-134).
 *
 * Sem servidor isso é controle operacional, não segurança — mas está no lugar certo:
 * no dia em que houver servidor, vira segurança real sem reescrever caso de uso
 * nenhum (ADR-102).
 */
export class PermissionChecker {
  can(session: Session, permission: PermissionKey, projectId?: ProjectId): boolean {
    const scope = projectId ?? session.projectId;
    const effective = resolveEffectivePermissions(session.assignments, session.roles, scope);
    return effective.has(permission);
  }

  /** Todas as permissões efetivas — usado pela interface para esconder ações. */
  effectivePermissions(session: Session, projectId?: ProjectId): ReadonlySet<PermissionKey> {
    const scope = projectId ?? session.projectId;
    return resolveEffectivePermissions(session.assignments, session.roles, scope);
  }

  /**
   * Tem esta permissão em **alguma** obra?
   *
   * Só a navegação usa: esconder um item de menu que o perfil nunca poderá abrir. Toda
   * decisão de autorização continua sendo por obra — este método não autoriza nada.
   */
  canAnywhere(session: Session, permission: PermissionKey): boolean {
    return this.allAnywhere(session).has(permission);
  }

  /** O conjunto inteiro — a navegação decide vários itens de uma vez. */
  allAnywhere(session: Session): ReadonlySet<PermissionKey> {
    return permissionsAnywhere(session.assignments, session.roles);
  }

  /**
   * A permissão vem de papel **global**? (RN-133)
   *
   * `can` cai no `session.projectId` quando nenhum escopo é passado, e a sessão da tela
   * sempre carrega a obra ativa — então "sem escopo" acabava significando "na obra
   * aberta". Para operação de sistema isso é frouxo demais: quem administra a estrutura
   * de uma obra ganharia o catálogo global junto. Aqui o escopo é explicitamente
   * ausente, e só papel global conta.
   */
  canGlobally(session: Session, permission: PermissionKey): boolean {
    return resolveEffectivePermissions(session.assignments, session.roles, undefined).has(
      permission,
    );
  }

  ensureGlobally(session: Session, permission: PermissionKey): Result<void, AppError> {
    if (this.canGlobally(session, permission)) return ok(undefined);
    return err(
      forbiddenError(ERROR_CODE.PERMISSION_DENIED, ERROR_MESSAGE[ERROR_CODE.PERMISSION_DENIED]!, {
        permission,
        userId: session.userId,
        scope: 'global',
      }),
    );
  }

  ensure(
    session: Session,
    permission: PermissionKey,
    projectId?: ProjectId,
  ): Result<void, AppError> {
    if (this.can(session, permission, projectId)) return ok(undefined);
    return err(
      forbiddenError(ERROR_CODE.PERMISSION_DENIED, ERROR_MESSAGE[ERROR_CODE.PERMISSION_DENIED]!, {
        permission,
        userId: session.userId,
      }),
    );
  }

  /**
   * O usuário alcança esta obra? (RN-132)
   *
   * Verificado **antes** de ler o registro. A mensagem é deliberadamente igual à de
   * "obra não encontrada": distinguir vazaria a existência da obra.
   */
  ensureProjectAccess(session: Session, projectId: ProjectId): Result<void, AppError> {
    if (hasProjectAccess(session.assignments, projectId)) return ok(undefined);
    return err(
      forbiddenError(
        ERROR_CODE.PROJECT_ACCESS_DENIED,
        ERROR_MESSAGE[ERROR_CODE.PROJECT_ACCESS_DENIED]!,
        { projectId },
      ),
    );
  }

  /** Permissão e escopo de obra em uma chamada — o caminho padrão dos casos de uso. */
  ensureForProject(
    session: Session,
    permission: PermissionKey,
    projectId: ProjectId,
  ): Result<void, AppError> {
    const access = this.ensureProjectAccess(session, projectId);
    if (!access.ok) return access;
    return this.ensure(session, permission, projectId);
  }
}
